/// <reference types="google.maps" />

import React, {useEffect, useState, useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Box,
  Center,
  Container,
  Flex,
  HStack,
  useTheme,
  VStack,
  List,
  ListItem,
} from '@chakra-ui/react';
import {TextG10, TextG12, TextG14, H3} from '../../../components/typography';
import {StyledInput} from '../Edit';
import {StyledLabel} from '../../../components/Input';
import {BlueButton} from '../../../components/button';
import BackButton from '../../../components/BackButton';
import StateSelect from '../../../components/StateSelect';
import {
  useAddressTypes,
  useAddUserAddress,
  useGetUserAddresses,
  useUpdateUserAddress,
} from '../../../hooks/useUserAddress';
import {find} from 'lodash';
import useAuth from '../../../hooks/useAuth';
import {SpinnerLoader} from '../../../components/Loader';
import {AddressTypes} from '../../../types/address';
import {useAutocompleteSuggestions} from '../../../hooks/useAutocompleteSuggestions';
import {useStatesList} from '../../../hooks/useStateLIst';
import {ToastMessage} from '../../../components/toast';

const ZIP_LENGTH = 5;

const stateSelectStyle = {
  container: (baseStyles, state) => ({
    ...baseStyles,
    maxHeight: '32px',
    marginTop: '0px !important',
    borderBottom: '1px solid rgb(226, 232, 240)',
  }),
  control: (baseStyles, state) => ({
    ...baseStyles,
    width: '100px',
    backgroundColor: 'transparent',
    borderWidth: '0px',
  }),
  indicatorSeparator: () => ({
    width: '0px',
  }),
  loadingIndicator: (baseStyles, state) => ({
    ...baseStyles,
    display: 'none',
  }),
  menu: (baseStyles, state) => ({
    ...baseStyles,
    marginBottom: '0px',
  }),
  menuList: (baseStyles, state) => ({
    ...baseStyles,
    maxHeight: '155px',
    fontSize: '10px',
    fontFamily: 'Gordita',
  }),
  singleValue: (baseStyles, state) => ({
    ...baseStyles,
    fontSize: '14px',
    fontFamily: 'Gordita',
  }),
  valueContainer: (baseStyles, state) => ({
    ...baseStyles,
    paddingLeft: '0px',
  }),
};

type FieldError = {[index: string]: string};

function EditHomeAddress() {
  const navigate = useNavigate();
  const {user, reFetchUserData} = useAuth();
  const theme = useTheme();
  const [address, setAddress] = useState<any>();
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<FieldError>({});
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressInput, setAddressInput] = useState('');

  // Memoize the request options to prevent infinite API calls
  const autocompleteOptions = useMemo(
    () => ({
      includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
      includedRegionCodes: ['us'],
      locationBias: {
        north: 33.3, // Oceanside/North County
        south: 32.5, // Chula Vista/near border
        east: -116.7, // Alpine/East County
        west: -117.6, // Pacific Ocean
      },
    }),
    []
  );

  // Use the new Autocomplete Data API with San Diego metro area location bias
  const {suggestions, resetSession} = useAutocompleteSuggestions(addressInput, autocompleteOptions);

  // Filter to only show place predictions
  const placePredictions = useMemo(
    () => suggestions.filter((s) => s.placePrediction).map((s) => s.placePrediction!),
    [suggestions]
  );

  const {getIdForAddressType, addressTypesIsLoading} = useAddressTypes();
  const addUserAddress = useAddUserAddress();
  const updateUserAddress = useUpdateUserAddress();
  const {statesList, stateListIsLoading} = useStatesList();

  const {userAddresses, userAddressesLoading} = useGetUserAddresses({userId: user?.id});
  const currentAddress = find(userAddresses, {
    type_id: getIdForAddressType(AddressTypes.Home),
  });

  const setFieldErrors = (field, error) => {
    setErrors({
      ...errors,
      [field]: error,
    });
  };

  useEffect(() => {
    if (address)
      isEmpty(address.street_address)
        ? setFieldErrors('street_address', 'Street Address field is empty')
        : setFieldErrors('street_address', '');
  }, [address?.street_address]);

  useEffect(() => {
    if (address)
      isEmpty(address.city) || isEmpty(address.city.name)
        ? setFieldErrors('city', 'City field is empty')
        : setFieldErrors('city', '');
  }, [address?.city]);

  useEffect(() => {
    if (address)
      isEmpty(address.state)
        ? setFieldErrors('state', 'State field is empty')
        : setFieldErrors('state', '');
  }, [address?.state]);

  useEffect(() => {
    if (address)
      isEmpty(address.zip_code)
        ? setFieldErrors('zip_code', 'Zip Code field is empty')
        : setFieldErrors('zip_code', '');
  }, [address?.zip_code]);

  const handleSaveAddress = async () => {
    if (!address.zip_code || address.zip_code.length < ZIP_LENGTH) {
      setFieldErrors('zip', 'Zip must be at least 5 numbers');
      return;
    } else {
      setFieldErrors('zip', '');
    }

    setIsSaving(true);
    if (user?.id) {
      if (currentAddress) {
        if (address && address.street_address && address.state) {
          await updateUserAddress.mutateAsync({
            userId: user?.id,
            addressId: currentAddress.id,
            address: {...address, state_id: address.state.id, city: address.city.name},
          });
        }
      } else {
        if (address && address.street_address && address.state) {
          const typeId = getIdForAddressType(AddressTypes.Home);
          await addUserAddress.mutateAsync({
            userId: user?.id,
            address: {
              ...address,
              type_id: typeId,
              state_id: address.state.id,
              city: address.city.name,
            },
          });
        }
      }
      await reFetchUserData();
      
      ToastMessage({
        status: 'success',
        text: 'Home address saved successfully!',
        showStatusIcon: true,
      });
    }
    setIsSaving(false);
  };

  const handleSuggestionClick = useCallback(
    async (suggestion: google.maps.places.PlacePrediction) => {
      const place = suggestion.toPlace();

      // Fetch place details including address components
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress'],
      });

      if (!place.addressComponents) return;

      // Extract address components
      let streetNumber = '';
      let route = '';
      let unit = '';
      let city = '';
      let state = '';
      let zipCode = '';

      for (const component of place.addressComponents) {
        const types = component.types;
        if (types.includes('street_number')) {
          streetNumber = component.longText || '';
        } else if (types.includes('route')) {
          route = component.longText || '';
        } else if (types.includes('subpremise')) {
          unit = component.longText || '';
        } else if (types.includes('locality')) {
          city = component.longText || '';
        } else if (types.includes('administrative_area_level_1')) {
          state = component.shortText || '';
        } else if (types.includes('postal_code')) {
          zipCode = component.longText || '';
        }
      }

      const streetAddress = `${streetNumber} ${route}`.trim();

      // Find the actual state object from our state list by abbreviation
      const stateObj = statesList?.find((s: any) => s.abbreviation === state);

      setAddress((prev) => ({
        ...prev,
        street_address: streetAddress,
        unit: unit,
        city: {name: city},
        state: stateObj,
        zip_code: zipCode,
      }));

      setAddressInput(streetAddress);
      setShowSuggestions(false);

      // Reset session token after place selection (important for billing)
      resetSession();
    },
    [resetSession]
  );

  const handleAddressInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddressInput(value);
    setAddress((prev) => ({
      ...prev,
      street_address: value,
    }));
    setShowSuggestions(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    // Delay hiding suggestions to allow click events to fire
    setTimeout(() => setShowSuggestions(false), 200);
  }, []);

  useEffect(() => {
    if (!userAddressesLoading && !addressTypesIsLoading) {
      const currentAddr =
        find(userAddresses, {type_id: getIdForAddressType(AddressTypes.Home)}) || {};
      setAddress(currentAddr);
      setAddressInput(currentAddr.street_address || '');
    }
  }, [userAddresses, addressTypesIsLoading]);

  const isFormValid =
    address &&
    !isEmpty(address.street_address) &&
    !isEmpty(address.state) &&
    !isEmpty(address.zip_code) &&
    !isEmpty(address.city) &&
    !isEmpty(address.city.name);

  if (userAddressesLoading || addressTypesIsLoading || stateListIsLoading) {
    return (
      <Container>
        <Center h="100vh">
          <SpinnerLoader />
        </Center>
      </Container>
    );
  }

  return (
    <Container>
      <Box pt={6}>
        <Flex alignItems="center">
          <BackButton pos={'relative'} onClick={() => navigate('/home')} sticky={false} />
          <Box flex="1">
            <Center ml="-35px">
              <H3 color={theme.colors.darkGrey}>
                {currentAddress ? 'Update' : 'Add'} Home Address
              </H3>
            </Center>
          </Box>
        </Flex>
      </Box>

      <Box mt={8} borderRadius="12px" p={5} background={theme.colors.veryLightBlue}>
        <TextG14 fontWeight="700" color={theme.colors.darkGrey} mb={2}>
          Home Address
        </TextG14>
        <TextG12 color={theme.colors.grey} mb={6}>
          Adding your home address helps us personalize your experience and show you relevant local
          events and offers.
        </TextG12>

        <VStack spacing="20px">
          <Box width="100%" position="relative">
            <StyledInput
              autoComplete="off"
              id="address-line1"
              label="Address"
              name="address"
              onChange={handleAddressInputChange}
              onFocus={() => setShowSuggestions(true)}
              onBlur={handleInputBlur}
              value={addressInput}
              placeholder="Start typing your address..."
            />
            {showSuggestions && placePredictions.length > 0 && (
              <Box position="absolute" top="100%" left={0} right={0} zIndex={1000} mt={1}>
                <List
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="4px"
                  boxShadow="0 2px 6px rgba(0,0,0,0.3)"
                  maxH="300px"
                  overflowY="auto">
                  {placePredictions.map((prediction) => (
                    <ListItem
                      key={prediction.placeId}
                      px={4}
                      py={2.5}
                      cursor="pointer"
                      fontSize="14px"
                      color={theme.colors.gray?.[700] || '#333'}
                      transition="background-color 0.1s"
                      _hover={{bg: theme.colors.gray?.[100] || 'gray.50'}}
                      onClick={() => handleSuggestionClick(prediction)}>
                      {prediction.text?.text || ''}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>

          <StyledInput
            autoComplete="shipping address-line2"
            id="address-line2"
            label="Unit"
            name="address2"
            onChange={(e) => {
              setAddress((prev) => {
                return {...prev, unit: e.target.value};
              });
            }}
            value={address?.unit || ''}
          />

          <HStack spacing="22px" mt="20px">
            <StyledInput
              pr={0}
              autoComplete="shipping address-level2"
              id="city"
              label="City"
              name="city"
              onChange={(e) => {
                setAddress((prev) => {
                  return {...prev, city: {name: e.target.value}};
                });
              }}
              value={address?.city?.name || ''}
            />
            <VStack alignItems="flex-start">
              <StyledLabel fontSize="10px" mb={0} color={theme.colors.grey}>
                State
              </StyledLabel>
              <StateSelect
                hidePlaceholder
                styles={stateSelectStyle}
                changeHandler={(e) => {
                  setAddress((prev) => {
                    return {...prev, state: e};
                  });
                }}
                value={address?.state}
              />
            </VStack>
            <StyledInput
              type="number"
              autoComplete="shipping postal-code"
              id="zip"
              label="Zip code"
              name="zip"
              onChange={(e) => {
                if (e.target.value.length > ZIP_LENGTH) return;
                setAddress((prev) => {
                  return {...prev, zip_code: e.target.value};
                });
              }}
              value={address?.zip_code || ''}
            />
          </HStack>

          {errors &&
            Object.values(errors).map((error, index) => {
              if (error) {
                return (
                  <Center key={index} mt="10px !important">
                    <TextG12 color={theme.colors.rose}>{error}</TextG12>
                  </Center>
                );
              }
            })}

          <BlueButton
            isDisabled={!isFormValid}
            w="100%"
            borderRadius="7px"
            onClick={handleSaveAddress}>
            {isSaving ? <SpinnerLoader /> : 'Save Address'}
          </BlueButton>
        </VStack>
      </Box>
    </Container>
  );
}

const isEmpty = (str: string) => {
  return !str || str === '';
};

export default EditHomeAddress;
